import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ContentCard } from './ContentCard'
import type { Content } from '@/types'

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']

interface PEDCalendarProps {
  contents: Content[]
  year: number
  month: number // 0-based (0 = January)
  onContentClick: (content: Content) => void
  onDayClick?: (date: Date) => void
  selectedDayStr?: string // YYYY-MM-DD of the currently selected day
  onDaySelect?: (date: Date) => void
}

function getDaysInMonth(year: number, month: number): Date[] {
  const days: Date[] = []
  const date = new Date(year, month, 1)
  while (date.getMonth() === month) {
    days.push(new Date(date))
    date.setDate(date.getDate() + 1)
  }
  return days
}

function getMonthStartOffset(year: number, month: number): number {
  const firstDay = new Date(year, month, 1).getDay()
  // Convert Sunday=0 to Monday=0 (ISO week)
  return firstDay === 0 ? 6 : firstDay - 1
}

// Format date as YYYY-MM-DD in local timezone (avoids UTC shift)
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function PEDCalendar({ contents, year, month, onContentClick, onDayClick, selectedDayStr, onDaySelect }: PEDCalendarProps) {
  const today = new Date()
  const todayStr = toLocalDateStr(today)

  const days = getDaysInMonth(year, month)
  const startOffset = getMonthStartOffset(year, month)

  // Group contents by date (YYYY-MM-DD in local timezone)
  const contentsByDate = contents.reduce<Record<string, Content[]>>((acc, c) => {
    if (!c.scheduledAt) return acc
    const dateKey = toLocalDateStr(new Date(c.scheduledAt))
    if (!acc[dateKey]) acc[dateKey] = []
    acc[dateKey].push(c)
    return acc
  }, {})

  // Build grid cells: empty cells for offset, then day cells
  const totalCells = Math.ceil((days.length + startOffset) / 7) * 7

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-card overflow-hidden">
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-slate-200">
        {DAY_LABELS.map((label) => (
          <div
            key={label}
            className="text-center py-1.5 sm:py-2 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wide"
          >
            <span className="sm:hidden">{label.charAt(0)}</span>
            <span className="hidden sm:inline">{label}</span>
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {Array.from({ length: totalCells }).map((_, idx) => {
          const dayIndex = idx - startOffset
          const isValidDay = dayIndex >= 0 && dayIndex < days.length

          if (!isValidDay) {
            return (
              <div
                key={`empty-${idx}`}
                className="min-h-[64px] sm:min-h-[100px] bg-slate-50/50 border-r border-b border-slate-100"
              />
            )
          }

          const day = days[dayIndex]!
          const dateKey = toLocalDateStr(day)
          const isToday = dateKey === todayStr
          const isSelected = selectedDayStr ? dateKey === selectedDayStr : false
          const dayContents = contentsByDate[dateKey] ?? []
          const isWeekend = day.getDay() === 0 || day.getDay() === 6

          return (
            <div
              key={dateKey}
              className={cn(
                'group/day relative min-h-[64px] sm:min-h-[100px] border-r border-b border-slate-100 p-1 sm:p-1.5 flex flex-col gap-0.5 sm:gap-1 cursor-pointer',
                isWeekend && 'bg-slate-50/60',
                isToday && !isSelected && 'bg-accent-50/30',
                isSelected && 'bg-primary-50/60 ring-1 ring-inset ring-primary-300'
              )}
              onClick={() => {
                if (onDaySelect) onDaySelect(day)
                if (onDayClick && dayContents.length === 0) onDayClick(day)
              }}
            >
              {/* Quick-add button (visible on hover, desktop only) */}
              {onDayClick && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDayClick(day) }}
                  className={cn(
                    'absolute top-1 left-1 w-5 h-5 rounded-full hidden sm:flex items-center justify-center',
                    'bg-accent-600 text-white shadow-sm',
                    'opacity-0 group-hover/day:opacity-100 transition-opacity',
                    'hover:bg-accent-700 z-10'
                  )}
                  title="Aggiungi contenuto"
                >
                  <Plus className="w-3 h-3" />
                </button>
              )}

              {/* Day number */}
              <div className="flex items-center justify-end mb-0.5">
                <span
                  className={cn(
                    'w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full text-[10px] sm:text-xs font-medium',
                    isToday
                      ? 'bg-accent-600 text-white'
                      : isSelected
                        ? 'bg-primary-600 text-white'
                        : 'text-slate-500 hover:bg-slate-100'
                  )}
                >
                  {day.getDate()}
                </span>
              </div>

              {/* Content cards — mobile: only count dots; desktop: compact cards */}
              <div className="hidden sm:flex flex-col gap-0.5 overflow-hidden">
                {dayContents.slice(0, 3).map((c) => (
                  <ContentCard
                    key={c.id}
                    content={c}
                    compact
                    onClick={() => onContentClick(c)}
                  />
                ))}
                {dayContents.length > 3 && (
                  <span className="text-[9px] text-slate-400 pl-1 font-medium">
                    +{dayContents.length - 3} altri
                  </span>
                )}
              </div>

              {/* Mobile: stack title + dot indicators */}
              <div className="flex sm:hidden flex-col gap-0.5 overflow-hidden">
                {dayContents.slice(0, 2).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onContentClick(c) }}
                    className="text-left text-[9px] text-slate-600 truncate leading-tight hover:text-accent-600"
                  >
                    • {c.title}
                  </button>
                ))}
                {dayContents.length > 2 && (
                  <span className="text-[9px] text-accent-600 font-medium">+{dayContents.length - 2}</span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
